import { escapeHtml } from "./html";

export interface AutocompleteItem {
  value: string;
  label: string;
  hint?: string;
}

export interface AutocompleteOptions<T extends AutocompleteItem> {
  input: HTMLInputElement;
  resultsContainer: HTMLElement;
  getItems: (query: string) => T[] | Promise<T[]>;
  onSelect: (item: T) => void;
  /** Show the full (unfiltered) list when the input is focused while empty. */
  showOnEmptyFocus?: boolean;
  emptyMessage?: string;
  /** Debounce input events by this many ms before calling getItems — for slow/async sources. */
  debounceMs?: number;
  /** Shown while an async getItems() call is pending. */
  loadingMessage?: string;
  /** Shown when getItems() rejects. */
  errorMessage?: string;
}

let idCounter = 0;

/**
 * A single reusable search-to-select widget, used both for the county search
 * in the subscription form and for the top-left map search. Supports mouse
 * and keyboard (Arrow Up/Down, Enter, Escape) selection with ARIA combobox
 * semantics, so it's usable without a mouse.
 *
 * getItems may return a Promise (e.g. a network-backed search) — responses to
 * stale queries (superseded by a newer keystroke) are discarded via requestSeq.
 */
export class Autocomplete<T extends AutocompleteItem> {
  private items: T[] = [];
  private highlightedIndex = -1;
  private readonly listboxId = `autocomplete-listbox-${idCounter++}`;
  private requestSeq = 0;
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(private readonly options: AutocompleteOptions<T>) {
    const { input, resultsContainer, debounceMs } = this.options;

    resultsContainer.id = this.listboxId;
    resultsContainer.setAttribute("role", "listbox");
    input.setAttribute("role", "combobox");
    input.setAttribute("aria-expanded", "false");
    input.setAttribute("aria-controls", this.listboxId);
    input.setAttribute("aria-autocomplete", "list");

    input.addEventListener("input", () => {
      if (debounceMs) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => void this.open(input.value), debounceMs);
      } else {
        void this.open(input.value);
      }
    });
    input.addEventListener("focus", () => {
      if (this.options.showOnEmptyFocus || input.value.trim()) {
        void this.open(input.value);
      }
    });
    input.addEventListener("keydown", (event) => this.handleKeydown(event));
    resultsContainer.addEventListener("mousedown", (event) => this.handleMousedown(event));
    document.addEventListener("click", (event) => {
      const target = event.target as Node;
      if (!input.contains(target) && !resultsContainer.contains(target)) {
        this.close();
      }
    });
  }

  /** Re-render with the current input value — call after external state changes (e.g. a chip was removed). */
  refresh(): void {
    if (!this.options.resultsContainer.classList.contains("hidden")) {
      void this.open(this.options.input.value);
    }
  }

  close(): void {
    const { input, resultsContainer } = this.options;
    clearTimeout(this.debounceTimer);
    this.requestSeq++;
    resultsContainer.classList.add("hidden");
    resultsContainer.innerHTML = "";
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
    this.items = [];
    this.highlightedIndex = -1;
  }

  private async open(query: string): Promise<void> {
    const seq = ++this.requestSeq;
    const { resultsContainer, getItems, loadingMessage, input } = this.options;

    if (loadingMessage) {
      resultsContainer.innerHTML = `<div class="autocomplete-empty">${loadingMessage}</div>`;
      resultsContainer.classList.remove("hidden");
      input.setAttribute("aria-expanded", "true");
    }

    let items: T[];
    try {
      items = await getItems(query);
    } catch {
      if (seq !== this.requestSeq) return;
      resultsContainer.innerHTML = `<div class="autocomplete-empty">${this.options.errorMessage ?? "Nie udało się wyszukać."}</div>`;
      resultsContainer.classList.remove("hidden");
      input.setAttribute("aria-expanded", "true");
      this.items = [];
      this.highlightedIndex = -1;
      return;
    }
    if (seq !== this.requestSeq) return;

    this.items = items;
    this.highlightedIndex = -1;

    resultsContainer.innerHTML =
      this.items
        .map(
          (item, index) =>
            `<div class="autocomplete-item" id="${this.optionId(index)}" role="option" aria-selected="false" data-index="${index}">${escapeHtml(item.label)}${item.hint ? ` <span class="autocomplete-hint">${escapeHtml(item.hint)}</span>` : ""}</div>`,
        )
        .join("") || `<div class="autocomplete-empty">${this.options.emptyMessage ?? "Brak wyników"}</div>`;
    resultsContainer.classList.remove("hidden");
    input.setAttribute("aria-expanded", "true");
  }

  private optionId(index: number): string {
    return `${this.listboxId}-option-${index}`;
  }

  private updateHighlight(): void {
    const { resultsContainer, input } = this.options;
    resultsContainer.querySelectorAll(".autocomplete-item").forEach((element, index) => {
      element.classList.toggle("highlighted", index === this.highlightedIndex);
      element.setAttribute("aria-selected", String(index === this.highlightedIndex));
    });
    if (this.highlightedIndex >= 0) {
      input.setAttribute("aria-activedescendant", this.optionId(this.highlightedIndex));
      resultsContainer
        .querySelector(`#${CSS.escape(this.optionId(this.highlightedIndex))}`)
        ?.scrollIntoView({ block: "nearest" });
    } else {
      input.removeAttribute("aria-activedescendant");
    }
  }

  private handleKeydown(event: KeyboardEvent): void {
    const isOpen = !this.options.resultsContainer.classList.contains("hidden");

    if (event.key === "Escape") {
      if (isOpen) {
        this.close();
        event.preventDefault();
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!isOpen) {
        void this.open(this.options.input.value).then(() => {
          if (this.items.length > 0) {
            this.highlightedIndex = 0;
            this.updateHighlight();
          }
        });
        return;
      }
      if (this.items.length > 0) {
        this.highlightedIndex = (this.highlightedIndex + 1) % this.items.length;
        this.updateHighlight();
      }
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (isOpen && this.items.length > 0) {
        this.highlightedIndex = (this.highlightedIndex - 1 + this.items.length) % this.items.length;
        this.updateHighlight();
      }
      return;
    }

    if (event.key === "Enter" && isOpen) {
      event.preventDefault();
      if (this.highlightedIndex >= 0) {
        this.select(this.items[this.highlightedIndex]);
      }
    }
  }

  private handleMousedown(event: MouseEvent): void {
    const target = (event.target as HTMLElement).closest<HTMLElement>(".autocomplete-item[data-index]");
    if (!target) return;
    const index = Number(target.dataset.index);
    const item = this.items[index];
    if (item) this.select(item);
  }

  private select(item: T): void {
    this.options.onSelect(item);
    this.close();
  }
}
